<template>
  <Navbar />
  <div class="password-container body auth-page-skin" :dir="rtl ? 'rtl' : 'ltr'">

    <div class="password-card auth-panel">
      <h2>{{ $t('resetPassword.title') }}</h2>
      <p class="subtitle">{{ $t('resetPassword.subtitle') }}</p>

      <form @submit.prevent="handleResetPassword">

        <div class="form-group">
          <label class="form-label">
            {{ $t('resetPassword.newPassword') }}
          </label>

          <input
            v-model="newPassword"
            type="password"
            class="form-input"
            :placeholder="$t('resetPassword.newPasswordPlaceholder')"
            @input="checkPasswordStrength"
          />

          <div class="password-strength">
            <div class="strength-bar" :style="strengthBarStyle"></div>
            <div class="strength-text">{{ strengthText }}</div>
          </div>

          <div class="password-requirements">
            <p :class="lengthValid ? 'valid' : 'invalid'">
              {{ $t('resetPassword.lengthRequirement') }}
            </p>

            <p :class="letterValid ? 'valid' : 'invalid'">
              {{ $t('resetPassword.letterRequirement') }}
            </p>

            <p :class="numberValid ? 'valid' : 'invalid'">
              {{ $t('resetPassword.numberRequirement') }}
            </p>
          </div>
        </div>

        <div class="form-group">
          <label class="form-label">
            {{ $t('resetPassword.confirmPassword') }}
          </label>

          <input
            v-model="confirmPassword"
            type="password"
            class="form-input"
            :placeholder="$t('resetPassword.confirmPasswordPlaceholder')"
            @input="checkPasswordMatch"
          />

          <div class="confirm-message" :class="passwordMatch ? 'valid' : 'invalid'">
            {{ confirmMessage }}
          </div>
        </div>

        <button
          type="submit"
          class="submit-btn"
          :disabled="!isFormValid || loading"
        >
          {{ loading ? $t('common.loading') : $t('resetPassword.resetPassword') }}
        </button>

        <div class="back-link">
          <router-link to="/reset-otp">
            {{ $t('resetPassword.back') }}
          </router-link>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import Navbar from '@/components/navbar_styled_register.vue';

export default {
  name: 'ResetNewPassword',
  components: { Navbar },
  setup() {
    const router = useRouter()
    const newPassword = ref('')
    const confirmPassword = ref('')
    const loading = ref(false)
    const currentLang = ref('fa')
    const phoneNumber = ref('')
    const otpCode = ref('')

    // Password strength states
    const lengthValid = ref(false)
    const letterValid = ref(false)
    const numberValid = ref(false)
    const passwordMatch = ref(false)

    const translations = {
      en: {
        passwordResetTitle: "Set New Password",
        subtitle: "Create a new password for your account",
        newPassword: "New Password",
        newPasswordPlaceholder: "Enter new password",
        confirmPassword: "Confirm Password",
        confirmPasswordPlaceholder: "Confirm new password",
        resetPassword: "Reset Password",
        lengthRequirement: "At least 8 characters",
        letterRequirement: "Contains letters",
        numberRequirement: "Contains numbers",
        strengthWeak: "Weak",
        strengthMedium: "Medium",
        strengthStrong: "Strong",
        passwordMatch: "Passwords match",
        passwordMismatch: "Passwords do not match",
        back: "Back to OTP"
      },
      fa: {
        passwordResetTitle: "تنظیم رمز عبور جدید",
        subtitle: "یک رمز عبور جدید برای حساب خود ایجاد کنید",
        newPassword: "رمز عبور جدید",
        newPasswordPlaceholder: "رمز عبور جدید را وارد کنید",
        confirmPassword: "تکرار رمز عبور",
        confirmPasswordPlaceholder: "تکرار رمز عبور جدید",
        resetPassword: "بازنشانی رمز عبور",
        lengthRequirement: "حداقل ۸ کاراکتر",
        letterRequirement: "حاوی حروف",
        numberRequirement: "حاوی اعداد",
        strengthWeak: "ضعیف",
        strengthMedium: "متوسط",
        strengthStrong: "قوی",
        passwordMatch: "رمز عبور مطابقت دارد",
        passwordMismatch: "رمز عبور مطابقت ندارد",
        back: "بازگشت به کد تأیید"
      },
      ar: {
        passwordResetTitle: "تعيين كلمة مرور جديدة",
        subtitle: "أنشئ كلمة مرور جديدة لحسابك",
        newPassword: "كلمة المرور الجديدة",
        newPasswordPlaceholder: "أدخل كلمة المرور الجديدة",
        confirmPassword: "تأكيد كلمة المرور",
        confirmPasswordPlaceholder: "تأكيد كلمة المرور الجديدة",
        resetPassword: "إعادة تعيين كلمة المرور",
        lengthRequirement: "8 أحرف على الأقل",
        letterRequirement: "يحتوي على حروف",
        numberRequirement: "يحتوي على أرقام",
        strengthWeak: "ضعيف",
        strengthMedium: "متوسط",
        strengthStrong: "قوي",
        passwordMatch: "كلمات المرور متطابقة",
        passwordMismatch: "كلمات المرور غير متطابقة",
        back: "العودة إلى رمز التحقق"
      }
    }

    const direction = computed(() => {
      return currentLang.value === 'fa' || currentLang.value === 'ar' ? 'rtl' : 'ltr'
    })

    const strength = computed(() => {
      let score = 0
      if (lengthValid.value) score++
      if (letterValid.value) score++
      if (numberValid.value) score++
      return score
    })

    const strengthText = computed(() => {
      if (newPassword.value.length === 0) return ''
      const texts = translations[currentLang.value]
      switch (strength.value) {
        case 1: return texts.strengthWeak
        case 2: return texts.strengthMedium
        case 3: return texts.strengthStrong
        default: return ''
      }
    })

    const strengthBarStyle = computed(() => {
      const width = (strength.value / 3) * 100
      let color = '#e5e7eb'
      
      if (newPassword.value.length > 0) {
        switch (strength.value) {
          case 1: color = '#dc2626'; break
          case 2: color = '#f59e0b'; break
          case 3: color = '#059669'; break
        }
      }
      
      return {
        width: `${width}%`,
        backgroundColor: color
      }
    })

    const confirmMessage = computed(() => {
      const texts = translations[currentLang.value]
      if (confirmPassword.value.length === 0) return ''
      return passwordMatch.value ? texts.passwordMatch : texts.passwordMismatch
    })

    const isFormValid = computed(() => {
      return  passwordMatch.value
    })

    const checkPasswordStrength = () => {
      const pwd = newPassword.value
      lengthValid.value = pwd.length >= 8
      letterValid.value = /[a-zA-Zآ-ی]/.test(pwd)
      numberValid.value = /[0-9]/.test(pwd)
      checkPasswordMatch()
    }

    const checkPasswordMatch = () => {
      passwordMatch.value = newPassword.value === confirmPassword.value && newPassword.value.length > 0
    }

    const switchLanguage = () => {
      localStorage.setItem('selectedLanguage', currentLang.value)
    }

    const handleResetPassword = async () => {
      if (!isFormValid.value) return

      loading.value = true

      try {
        const response = await api.post('/reset-password', {
          phone_number: phoneNumber.value,
          otp_code: otpCode.value,
          new_password: newPassword.value
        })

        if (response.data.success) {
          alert(response.data.message || translations[currentLang.value].passwordResetSuccess || 'Password has been reset successfully!')
          
          // Clear reset data from localStorage
          localStorage.removeItem('resetPhoneNumber')
          localStorage.removeItem('resetOtp')
          
          // Redirect to login page
          router.push('/login')
        } else {
          throw new Error(response.data.message)
        }
      } catch (err) {
        alert(err.response?.data?.message || 'Password reset failed. Please try again.')
      } finally {
        loading.value = false
      }
    }

    onMounted(() => {
      // Get phone number and OTP from localStorage
      const savedPhone = localStorage.getItem('resetPhoneNumber')
      const savedOtp = localStorage.getItem('resetOtp')

      if (!savedPhone || !savedOtp) {
        router.push('/reset-password')
        return
      }

      phoneNumber.value = savedPhone
      otpCode.value = savedOtp

      const savedLang = localStorage.getItem('selectedLanguage') || 'fa'
      currentLang.value = savedLang
    })

    return {
      newPassword,
      confirmPassword,
      loading,
      currentLang,
      translations,
      direction,
      lengthValid,
      letterValid,
      numberValid,
      passwordMatch,
      strengthText,
      strengthBarStyle,
      confirmMessage,
      isFormValid,
      checkPasswordStrength,
      checkPasswordMatch,
      switchLanguage,
      handleResetPassword
    }
  }
}
</script>

<style>
.password-container{
  display:flex;
  justify-content:center;
  align-items:center;
  min-height:100vh;
  background:var(--bg);
  padding:1rem;
}

.password-card{
  background:var(--card-bg);
  color:var(--text);
  padding:2rem;
  border-radius:12px;
  border:1px solid var(--border);
  box-shadow:var(--shadow);
  width:100%;
  max-width:420px;
}

.password-card h2{
  text-align:center;
  color:var(--primary);
}

.subtitle{
  text-align:center;
  color:var(--text-secondary);
  margin-bottom:1.5rem;
}

.form-group{
  margin-bottom:1.5rem;
}

.form-label{
  display:block;
  margin-bottom:0.4rem;
  font-weight:500;
}

.form-input{
  width:100%;
  padding:0.75rem;
  border-radius:6px;
  border:1px solid var(--border);
  background:var(--input-bg);
  color:var(--text);
}

.password-strength{
  margin-top:0.5rem;
}

.strength-bar{
  height:6px;
  border-radius:4px;
  background:var(--border);
  transition:all .3s;
}

.strength-text{
  font-size:.8rem;
  margin-top:.2rem;
}

.password-requirements{
  margin-top:.5rem;
  font-size:.8rem;
  background:var(--bg-soft);
  padding:.5rem;
  border-radius:6px;
  border:1px solid var(--border);
}

.password-requirements p{
  margin:0.2rem 0;
}

.valid{
  color:var(--success);
}

.invalid{
  color:var(--danger);
}

.confirm-message{
  font-size:.8rem;
  margin-top:.3rem;
}

.submit-btn{
  width:100%;
  padding:.75rem;
  border:none;
  border-radius:6px;
  background:var(--primary);
  color:white;
  cursor:pointer;
  margin-top:1rem;
}

.submit-btn:disabled{
  background:var(--disabled);
}

.back-link{
  margin-top:1.4rem;
  text-align:center;
}

.back-link a{
  color:var(--primary);
}


/* Shared Register-style auth UI */
.auth-page-skin { --auth-text:#f1f7f5; --auth-muted:#9bb5b4; --auth-surface:rgba(12,42,51,.72); --auth-border:rgba(145,205,194,.2); --auth-input:rgba(5,24,31,.72); --auth-accent:#f07832; --auth-accent-soft:#ff9b5f; position:relative; isolation:isolate; min-height:calc(100svh - 64px); overflow:hidden; padding:56px 20px 72px; color:var(--auth-text); background:radial-gradient(circle at 72% 45%,rgba(35,110,118,.25),transparent 34%),linear-gradient(135deg,#06151e 0%,#08242e 52%,#071b25 100%); font-family:'Vazirmatn',Tahoma,'Segoe UI',sans-serif; }
.auth-page-skin::before { content:''; position:absolute; z-index:-1; inset:-12%; background:url('~@/assets/register-login-bg.svg') center / min(720px,92vw) auto no-repeat; opacity:.34; pointer-events:none; }
.auth-page-skin::after { content:''; position:absolute; z-index:-1; inset:20%; border-radius:50%; background:rgba(240,120,50,.12); filter:blur(70px); pointer-events:none; }
.auth-page-skin > * { position:relative; z-index:1; }
.auth-page-skin .auth-panel { position:relative; z-index:2; width:min(100%,440px); margin:0 auto; padding:36px 32px 30px; border:1px solid var(--auth-border); border-radius:22px; background:var(--auth-surface); color:var(--auth-text); box-shadow:0 24px 70px rgba(0,0,0,.28),inset 0 1px 0 rgba(255,255,255,.04); backdrop-filter:blur(14px); }
.auth-page-skin .auth-panel h2 { color:var(--auth-text); font-size:clamp(22px,3vw,29px); font-weight:800; }
.auth-page-skin .subtitle { color:var(--auth-muted); line-height:1.8; }
.auth-page-skin .form-group { margin-bottom:18px; }
.auth-page-skin .form-label { color:var(--auth-muted); font-size:13px; font-weight:600; }
.auth-page-skin .form-input, .auth-page-skin .otp-input { width:100%; min-height:46px; padding:11px 14px; border:1px solid rgba(145,205,194,.25); border-radius:10px; outline:none; background:var(--auth-input); color:var(--auth-text); font:inherit; transition:border-color .2s,box-shadow .2s,background .2s; }
.auth-page-skin .form-input::placeholder { color:#76918f; }
.auth-page-skin .form-input:focus, .auth-page-skin .otp-input:focus { border-color:var(--auth-accent); box-shadow:0 0 0 3px rgba(240,120,50,.16); background:rgba(5,24,31,.88); }
.auth-page-skin .form-input.input-error { border-color:#e07070; }
.auth-page-skin .btn-primary, .auth-page-skin .verify-btn, .auth-page-skin .login-btn, .auth-page-skin .reset-btn, .auth-page-skin .password-btn, .auth-page-skin .submit-btn { min-height:46px; border:0; border-radius:10px; background:var(--auth-accent); color:#fff; font:inherit; font-weight:700; cursor:pointer; box-shadow:0 8px 24px rgba(240,120,50,.2); transition:transform .18s,background .18s,opacity .18s,box-shadow .18s; }
.auth-page-skin .btn-primary:hover:not(:disabled), .auth-page-skin .verify-btn:hover:not(:disabled), .auth-page-skin .login-btn:hover:not(:disabled), .auth-page-skin .reset-btn:hover:not(:disabled), .auth-page-skin .password-btn:hover:not(:disabled), .auth-page-skin .submit-btn:hover:not(:disabled) { transform:translateY(-2px); background:var(--auth-accent-soft); box-shadow:0 12px 32px rgba(240,120,50,.3); }
.auth-page-skin button:disabled { opacity:.65; cursor:wait; }
.auth-page-skin .text-danger, .auth-page-skin .error-message { color:#f28a8a; }
.auth-page-skin .text-success, .auth-page-skin .success-message { color:#7ed6a2; }
.auth-page-skin .error-message, .auth-page-skin .success-message { background:rgba(5,24,31,.48); border-color:var(--auth-border); }
.auth-page-skin .btn-secondary { background:#31545b; color:#fff; border:0; }
.auth-page-skin .btn-link, .auth-page-skin a { color:var(--auth-accent-soft); }
.auth-page-skin .role-selection-content, .auth-page-skin .company-selection-content { background:#0d2b35; color:var(--auth-text); border:1px solid var(--auth-border); }
.auth-page-skin .company-option { border-color:var(--auth-border); background:rgba(5,24,31,.38); }
@media (max-width:767px) { .auth-page-skin { min-height:calc(100svh - 56px); padding:34px 14px 40px; } .auth-page-skin::before { background-size:min(560px,125vw) auto; opacity:.28; } .auth-page-skin .auth-panel { width:min(100%,440px); padding:28px 22px; } }


/* Match Register composition: visual left, auth panel right; visual behind panel on mobile. */
.otp-container.auth-page-skin, .verify-container.auth-page-skin, .password-container.auth-page-skin, .reset-container.auth-page-skin {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr);
  align-items: center;
  gap: clamp(24px, 4vw, 72px);
  width: 100%;
  min-height: calc(100svh - 64px);
  padding: 72px clamp(24px, 5vw, 88px) 32px;
}
.otp-container.auth-page-skin::before, .verify-container.auth-page-skin::before, .password-container.auth-page-skin::before, .reset-container.auth-page-skin::before {
  inset: 8% 42% 4% 2%;
  background-size: min(680px, 56vw) auto;
  background-position: center;
  opacity: .62;
}
.otp-container.auth-page-skin > .auth-panel, .verify-container.auth-page-skin > .auth-panel, .password-container.auth-page-skin > .auth-panel, .reset-container.auth-page-skin > .auth-panel {
  grid-column: 2;
  justify-self: end;
  width: min(100%, 500px);
}
.reset-container.auth-page-skin > .language-switcher { position: absolute; z-index: 3; top: 18px; inset-inline-start: 24px; }
@media (max-width: 767px) {
  .otp-container.auth-page-skin, .verify-container.auth-page-skin, .password-container.auth-page-skin, .reset-container.auth-page-skin {
    position: relative;
    display: block;
    min-height: calc(100svh - 56px);
    padding: 34px 14px 40px;
  }
  .otp-container.auth-page-skin::before, .verify-container.auth-page-skin::before, .password-container.auth-page-skin::before, .reset-container.auth-page-skin::before {
    inset: -8% -18%;
    background-size: min(560px, 125vw) auto;
    opacity: .34;
  }
  .otp-container.auth-page-skin > .auth-panel, .verify-container.auth-page-skin > .auth-panel, .password-container.auth-page-skin > .auth-panel, .reset-container.auth-page-skin > .auth-panel {
    position: relative;
    z-index: 2;
    width: min(100%, 440px);
    margin: 0 auto;
  }
}

/* RegisterCompanyPhone has an extra inner container around its panel. */
.auth-page-skin > .login-container {
  display: grid;
  grid-template-columns: minmax(0, 1.08fr) minmax(320px, .92fr);
  align-items: center;
  gap: clamp(24px, 4vw, 72px);
  min-height: calc(100svh - 64px);
  padding: 0;
  background: transparent;
}
.auth-page-skin > .login-container > .login-card {
  grid-column: 2;
  justify-self: end;
  width: min(100%, 500px);
  position: relative;
  z-index: 2;
  background: var(--auth-surface);
  border: 1px solid var(--auth-border);
  border-radius: 22px;
  color: var(--auth-text);
  box-shadow: 0 24px 70px rgba(0,0,0,.28);
  backdrop-filter: blur(14px);
}
@media (max-width: 767px) {
  .auth-page-skin > .login-container { display: block; min-height: calc(100svh - 56px); }
  .auth-page-skin > .login-container > .login-card { width: min(100%, 440px); margin: 0 auto; }
}


/* Keep auth panels translucent like Register so the visual fades through them. */
.auth-page-skin .auth-panel,
.auth-page-skin .login-card,
.register-page .form-card {
  background: transparent !important;
  backdrop-filter: blur(4px);
}

/* Center the visual directly behind the panel on phone widths. */
@media (max-width: 767px) {
  .auth-page-skin::before {
    inset: 0;
    background-position: center center;
    background-size: min(112vw, 440px) auto;
    opacity: .5;
  }

  .auth-page-skin .auth-panel,
  .auth-page-skin .login-card,
  .register-page .form-card {
    position: relative;
    z-index: 4;
    background: transparent !important;
    margin-top: 120px !important;
    box-shadow: 0 18px 44px rgba(0, 0, 0, .18);
  }
}

/* Login fields must stay dark, including browser autofill. */
.register-page .form-card .form-input,
.login-page .form-card .form-input,
.login-page input.form-input {
  background: #05181f !important;
  background-color: #05181f !important;
  color: #f1f7f5 !important;
}

.register-page .form-card .form-input:-webkit-autofill,
.login-page .form-card .form-input:-webkit-autofill,
.login-page input.form-input:-webkit-autofill,
.login-page .form-input:-webkit-autofill:hover,
.login-page .form-input:-webkit-autofill:focus {
  -webkit-text-fill-color: #f1f7f5 !important;
  -webkit-box-shadow: 0 0 0 1000px #05181f inset !important;
  box-shadow: 0 0 0 1000px #05181f inset !important;
  background-color: #05181f !important;
  caret-color: #f1f7f5;
}
.otp-container.auth-page-skin,
.verify-container.auth-page-skin,
.password-container.auth-page-skin,
.reset-container.auth-page-skin,
.login-container.auth-page-skin {
  height: auto !important;
  min-height: 100dvh !important;
}

</style>
